import {WalkOfShameParsedData} from "./WalkOfShameParsedData";
import {IBetWinData, IMToWildFeature, IWalkOfShameGameData} from "./IWalkOfShameData";
import {WalkOfShameDataParser} from "./WalkOfShameDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {WalkOfShameGameConfig} from "../../WalkOfShameGameConfig";
import {WalkOfShameGameMode} from "../WalkOfShameGameMode";
import {WalkOfShame} from "../../WalkOfShame";

export class WalkOfShameParsedGameData extends WalkOfShameParsedData {

    public readonly betWins: IBetWinData[];
    public readonly singleWin: number;
    public readonly playedBetValue: number;
    public readonly wasFeatureBuy: boolean;
    public readonly brokeTheBank: boolean;
    public readonly scatter2feature: string;

    // Calculated values
    public readonly currentWinRatioConfig: IWinRatioConfig | null;

    // To add Spin Data
    public readonly nearWinReels: boolean[];
    public readonly nearWinBigWinReels: boolean[];
    public readonly totalWaysWon: number;
    public readonly mToWildFeature: IMToWildFeature[];
    public readonly totalScatters: number;
    public readonly splitId: number;
    public readonly reelWinPositionsAll: boolean[][];

    // #156
    private _mToWildReels: number[] = [];

    constructor(rawData: IWalkOfShameGameData, parser: WalkOfShameDataParser) {
        super(rawData, parser);

        this.betWins = parser.parseBetWins(rawData);
        this.singleWin = rawData.totalSpinWinnings;
        this.playedBetValue = rawData.playedBetValue;
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.brokeTheBank = rawData.brokeTheBank;
        this.currentMultiplier = Math.max(1, rawData.fsMultiplier);
        this.scatter2feature = rawData.scatter2feature;

        // Calculated values.
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);

        // To add Spin Data
        this.nearWinReels = parser.parseNearWinReels(this.scatterPositions, this.wasFeatureBuy, this.mode);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
        this.totalWaysWon = this.getTotalWaysWon();
        this.mToWildFeature = this.prepareMToWildFeature();
        this.splitId = this.getSplitId(rawData.symbolSizes);
        this.reelWinPositionsAll = this.getReelWinPositionsAll();

        this.totalScatters = this.scatterPositions.filter((val: number) => {
            return (val > -1);
        }).length;

        if (this.mode === WalkOfShameGameMode.RESPIN || this.mode === WalkOfShameGameMode.SUPER_FREESPIN || this.mode === WalkOfShameGameMode.SUPER_FREESPIN_RESPIN) {
            this.reels[0][this.s2SymId + 1] = ArrayHelper.getRandomValue(["L1", "L2", "L3", "L4", "L5"]);
        }

        if (this.reels[0][0] === "S2") {
            this.reels[0][0] = ArrayHelper.getRandomValue(["L1", "L2", "L3", "L4", "L5"]);
        }
        if (this.reels[0][this.reels[0].length - 1] === "S2") {
            this.reels[0][this.reels[0].length - 1] = ArrayHelper.getRandomValue(["L1", "L2", "L3", "L4", "L5"]);
        }

        WalkOfShameGameConfig.IS_ANTICIPATING = false;
    }

    private getRandomBigWinNearWin(): boolean[] {
        const values = ArrayHelper.initArrayWithValues(WalkOfShameGameConfig.REELS_NUM, () => false);
        values[4] = this.getRandomBigWinTease();

        return values;
    }

    private getRandomBigWinTease(): boolean {
        if (this.wasFeatureBuy) {
            return false;
        }
        switch (this.mode) {
            case WalkOfShameGameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 35;
            case WalkOfShameGameMode.FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 50;
            case WalkOfShameGameMode.SUPER_FREESPIN:
                return false;
            default:
                return false;
        }
    }

    private getTotalWaysWon(): number {
        let retVal: number = 0;

        this.betWins.forEach((value: IBetWinData) => {
            retVal += value.numberOfCombinations;
        });

        return retVal;
    }

    private prepareMToWildFeature(): IMToWildFeature[] {
        const retVal: IMToWildFeature[] = [],
            reels: string[][] = this.reels;
        let isConnected: boolean,
            firstSymInReel: string;

        this._mToWildReels = [];

        WalkOfShameGameConfig.FULL_SYM_PRESENT_LAST_REEL = false;
        for (let reel: number = 1; reel < reels.length; reel++) {
            // continue, if there is already a sticky symbol present
            if (WalkOfShame.mToWild.ifFullSymExistsOnReel(reel)) {
                continue;
            }

            firstSymInReel = reels[reel][1];
            isConnected = true;
            for (let symId: number = 2; symId < reels[reel].length - 1; symId++) {
                if (!firstSymInReel.includes("M") || reels[reel][symId] !== firstSymInReel) {
                    isConnected = false;
                }
            }
            if (isConnected) {
                retVal.push({
                    reel: reel,
                    symbol: firstSymInReel
                });

                this._mToWildReels.push(reel);

                if (reel === 4) {
                    WalkOfShameGameConfig.FULL_SYM_PRESENT_LAST_REEL = true;
                }
            }
        }

        return retVal;
    }

    private getSplitId(symbolSizes: number[][]): number {
        if (this.s2SymId > -1) {
            for (let reel: number = 1; reel < WalkOfShameGameConfig.REELS_NUM; reel++) {
                if (WalkOfShame.mToWild.ifFullSymExistsOnReel(reel) || this._mToWildReels.indexOf(reel) > -1) {
                    continue;
                }
                for (let symId: number = 0; symId < 4; symId++) {
                    if (symbolSizes[reel][symId] === 2) {
                        return symId;
                    }
                }
            }
        }
        return -1;
    }

    // #87
    private getReelWinPositionsAll(): boolean[][] {
        let retVal: boolean[][] = [];

        if (this.betWins.length > 0) {
            retVal = JSON.parse(JSON.stringify(this.betWins[0].reelWinPositions));

            for (let ctr: number = 1; ctr < this.betWins.length; ctr++) {
                for (let reel: number = 0; reel < retVal.length; reel++) {
                    for (let symId: number = 0; symId < retVal[reel].length; symId++) {
                        if (this.betWins[ctr].reelWinPositions[reel][symId]) {
                            retVal[reel][symId] = true;
                        }
                    }
                }
            }

            this.scatterPositions.forEach((symId: number, reelId: number) => {
                if (symId > -1) {
                    retVal[reelId][symId - 1] = true;
                }
            });
        }

        return retVal;
    }
}